import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import PhotoUploader from '../../components/PhotoUploader';
import api from '../../api';
import { DEAL_BUTTONS, CATEGORY_GROUPS, FIELDS_BY_KIND_BUY, FIELDS_BY_KIND_RENT, FIELDS_BY_KIND_DAILY } from './adFormConfig';
import { trackPublishAd } from '../../utils/analytics';
import './CreateAdPage.css';

const STORAGE_KEY = 'dealzy_create_ad_draft';

// Backend DealType -> frontend deal-tab key (commercial deals share tabs with residential ones)
const DEAL_TYPE_TO_KEY = {
  Buy: 'buy',
  Rent: 'rent',
  RentDaily: 'daily',
  CommercialRent: 'rent',
  CommercialBuy: 'buy',
};

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

const loadPersistedState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error('Failed to parse persisted draft', e);
    return null;
  }
};

const CreateAdPage = () => {
  const { id: editId } = useParams();
  const isEditMode = !!editId;
  const location = useLocation();
  // Admin edits reuse this same form (via /admin/ads/edit/:id) but must not auto-publish
  // or overwrite the ad's current moderation status on save.
  const isAdminEdit = location.pathname.startsWith('/admin/');

  const persisted = isEditMode ? null : loadPersistedState();
  const [dealKey, setDealKey]       = useState(persisted?.dealKey ?? null);
  const [selected, setSelected]     = useState(persisted?.selected ?? null);
  const [draftId, setDraftId]       = useState(persisted?.draftId ?? null);
  const [formData, setFormData]     = useState(persisted?.formData ?? {});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]         = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [loadError, setLoadError]     = useState(null);
  const [contactErrors, setContactErrors] = useState({});
  const navigate = useNavigate();

  // Prefill contact fields from profile once, only when creating a new ad and they're not set yet
  useEffect(() => {
    if (isEditMode) return;
    if (formData.contactName || formData.contactPhone) return;
    api.get('/profile')
      .then(({ data }) => {
        setFormData(prev => ({
          ...prev,
          contactName: prev.contactName ?? (data.contactName || data.name || ''),
          contactPhone: prev.contactPhone ?? (data.phoneNumber || ''),
        }));
      })
      .catch(e => console.error('Failed to load profile for contact prefill', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode) return; // don't touch the "new ad" draft slot while editing an existing ad
    if (!dealKey) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ dealKey, selected, draftId, formData }));
  }, [isEditMode, dealKey, selected, draftId, formData]);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    setLoadingEdit(true);
    setLoadError(null);

    api.get(`/ads/${editId}/edit`)
      .then(({ data }) => {
        if (cancelled) return;
        const key = DEAL_TYPE_TO_KEY[data.dealType] ?? 'buy';
        const propertyKind = lowerFirst(data.propertyKind);
        const groups = CATEGORY_GROUPS[key] ?? [];
        // Some property kinds map to multiple categories differing only by objectType
        // (e.g. "в новостройке" vs "во вторичке"), so match on it too when present.
        const candidates = groups.flatMap(g => g.items).filter(i => i.propertyKind === propertyKind && i.dealType === data.dealType);
        const matched = candidates.length > 1 ? candidates.find(i => i.objectType === data.objectType) : candidates[0];
        // Ambiguous match (objectType unset/unmatched among 2+ candidates) must not silently
        // fall back to candidates[0] - that used to default imported apartments to "новостройка".
        const item = matched
          ?? (candidates.length === 1 ? candidates[0] : null)
          ?? { key: propertyKind, label: data.propertyKind, propertyKind, dealType: data.dealType };

        setDealKey(key);
        setSelected(item);
        setDraftId(data.id);
        setFormData(data);
      })
      .catch((e) => {
        console.error('Failed to load ad for edit', e);
        if (!cancelled) setLoadError('Не удалось загрузить объявление для редактирования.');
      })
      .finally(() => { if (!cancelled) setLoadingEdit(false); });

    return () => { cancelled = true; };
  }, [editId]);

  const configMap = dealKey === 'buy' ? FIELDS_BY_KIND_BUY : dealKey === 'daily' ? FIELDS_BY_KIND_DAILY : FIELDS_BY_KIND_RENT;
  const fields = selected ? (configMap[selected.propertyKind] ?? []) : [];

  const handleDealClick = (key) => {
    if (dealKey === key) return;
    setDealKey(key);
    setSelected(null);
    setResult(null);
    if (!isEditMode) {
      setDraftId(null);
      setFormData({});
    }
  };

  const handleCategoryClick = async (item) => {
    setSelected(item);
    setResult(null);

    // Editing an existing ad: kind switch is only applied to the ad on save (PUT),
    // so just swap the selected category and keep formData - matching field names
    // (rooms, constructionYear, hasParking, etc.) carry their values over automatically.
    if (isEditMode) return;

    setDraftId(null);
    setFormData({});
    try {
      const { data } = await api.post('/ads/draft', { propertyKind: item.propertyKind, dealType: item.dealType });
      setDraftId(data.id);
    } catch (e) {
      console.error('Failed to create draft', e);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateContacts = () => {
    const errors = {};
    if (!formData.contactName?.trim()) errors.contactName = 'Укажите имя для связи';
    if (!formData.contactPhone?.trim()) errors.contactPhone = 'Укажите телефон для связи';
    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!draftId) return;
    if (!validateContacts()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const payload = {
        ...formData,
        propertyKind: selected.propertyKind,
        dealType: selected.dealType,
        ...(selected.objectType ? { objectType: selected.objectType } : {}),
      };

      await api.put(`/ads/${draftId}`, payload);
      if (!isAdminEdit) {
        await api.post(`/ads/${draftId}/publish`);
        trackPublishAd({ id: draftId, propertyKind: selected.propertyKind, dealType: selected.dealType, price: formData.price });
      }

      localStorage.removeItem(STORAGE_KEY);
      navigate(isAdminEdit ? '/admin/ads' : '/ad/' + draftId);
    } catch (e) {
      const message = e.response?.data ?? 'Не удалось подключиться к серверу.';
      setResult({ ok: false, message: typeof message === 'string' ? message : JSON.stringify(message) });
    } finally {
      setSubmitting(false);
    }
  };

  const groups = dealKey ? CATEGORY_GROUPS[dealKey] : null;

  return (
    <div className="create-ad">
      <div className="create-ad__container">
        <h1 className="create-ad__title">{isEditMode ? 'Редактировать объявление' : 'Создать объявление'}</h1>

        {isEditMode && loadingEdit && (
          <p style={{ color: '#999', fontSize: 14 }}>Загрузка объявления...</p>
        )}

        {isEditMode && loadError && (
          <div className="create-ad__result create-ad__result--err">{loadError}</div>
        )}

        {!(isEditMode && loadingEdit) && (
          <>
            {/* Step 1 — deal type */}
            <div className="create-ad__deal-row">
              {DEAL_BUTTONS.map(btn => (
                <button
                  key={btn.key}
                  className={`create-ad__deal-btn ${dealKey === btn.key ? 'create-ad__deal-btn--active' : ''}`}
                  onClick={() => handleDealClick(btn.key)}
                >
                  {btn.label}
                </button>
              ))}
            </div>

            {/* Step 2 — category panel */}
            {groups && (
              <div className="create-ad__categories">
                {groups.map(group => (
                  <div key={group.heading} className="create-ad__cat-group">
                    <div className="create-ad__cat-heading">{group.heading}</div>
                    <div className="create-ad__cat-divider" />
                    <div className="create-ad__cat-items">
                      {group.items.map(item => (
                        <button
                          key={item.key}
                          className={`create-ad__cat-btn ${selected?.key === item.key ? 'create-ad__cat-btn--active' : ''}`}
                          onClick={() => handleCategoryClick(item)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Step 3 — form */}
        {selected && fields.length > 0 && (!isEditMode || !loadingEdit) && (
          <div className="create-ad__form">
            <h2 className="create-ad__form-title">{selected.label}</h2>

            {fields.map(field => (
              <FieldRow
                key={field.name}
                field={field}
                value={formData[field.name] ?? ''}
                onChange={handleChange}
              />
            ))}

            <div className="create-ad__field">
              <label className="create-ad__label">
                Имя для связи<span className="create-ad__required"> *</span>
              </label>
              <input
                className="create-ad__input"
                type="text"
                value={formData.contactName ?? ''}
                onChange={e => handleChange('contactName', e.target.value)}
              />
              {contactErrors.contactName && (
                <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 0' }}>{contactErrors.contactName}</p>
              )}
            </div>

            <div className="create-ad__field">
              <label className="create-ad__label">
                Телефон для связи<span className="create-ad__required"> *</span>
              </label>
              <input
                className="create-ad__input"
                type="text"
                value={formData.contactPhone ?? ''}
                onChange={e => handleChange('contactPhone', e.target.value)}
              />
              {contactErrors.contactPhone && (
                <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 0' }}>{contactErrors.contactPhone}</p>
              )}
            </div>

            <div className="create-ad__field">
              <label className="create-ad__label">Фото и планировка</label>
              {draftId
                ? <PhotoUploader adId={draftId} />
                : <p style={{ color: '#999', fontSize: 14 }}>Выберите категорию, чтобы загрузить фото</p>
              }
            </div>

            {result && (
              <div className={`create-ad__result ${result.ok ? 'create-ad__result--ok' : 'create-ad__result--err'}`}>
                {result.message}
              </div>
            )}

            <button
              className="create-ad__submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Отправка...' : 'Опубликовать'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const FieldRow = ({ field, value, onChange }) => {
  const { name, label, type, options, required } = field;

  return (
    <div className="create-ad__field">
      <label className="create-ad__label">
        {label}{required && <span className="create-ad__required"> *</span>}
      </label>

      {type === 'text' && (
        <input
          className="create-ad__input"
          type="text"
          value={value}
          onChange={e => onChange(name, e.target.value)}
        />
      )}

      {type === 'number' && (
        <input
          className="create-ad__input create-ad__input--short"
          type="number"
          value={value}
          onChange={e => onChange(name, e.target.value === '' ? '' : Number(e.target.value))}
        />
      )}

      {type === 'textarea' && (
        <textarea
          className="create-ad__input create-ad__input--textarea"
          value={value}
          rows={4}
          onChange={e => onChange(name, e.target.value)}
        />
      )}

      {type === 'select' && (
        <select
          className="create-ad__input create-ad__input--select"
          value={value}
          onChange={e => onChange(name, e.target.value)}
        >
          <option value="">— выбрать —</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}

      {type === 'boolean' && (
        <label className="create-ad__toggle">
          <input
            type="checkbox"
            checked={!!value}
            onChange={e => onChange(name, e.target.checked)}
          />
          <span className="create-ad__toggle-track">
            <span className="create-ad__toggle-thumb" />
          </span>
        </label>
      )}

      {type === 'address' && (
        <AddressAutocomplete
          onAddressSelect={addr => onChange(name, addr)}
          initialValue={value && typeof value === 'object' ? value : undefined}
        />
      )}
    </div>
  );
};

export default CreateAdPage;
